import { cn, formatRelativeTime } from "../../../lib/utils"
import { Badge } from "../../../components/ui/badge"
import { ENTRY_TYPE_CONFIG, type ChangelogEntry } from "../types"
import { ChangelogTypeIcon } from "./ChangelogTypeIcon"

interface ChangelogEntryCardProps {
  entry: ChangelogEntry
  compact?: boolean
}

export function ChangelogEntryCard({ entry, compact }: ChangelogEntryCardProps) {
  const typeConfig = ENTRY_TYPE_CONFIG[entry.type]

  if (compact) {
    return (
      <div className="flex items-start gap-3 px-4 py-3 hover:bg-muted transition-colors">
        <ChangelogTypeIcon type={entry.type} size="sm" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn("text-[10px] font-semibold uppercase tracking-wider", typeConfig.color)}>
              {typeConfig.label}
            </span>
            {entry.version && (
              <span className="text-[10px] text-muted-foreground">{entry.version}</span>
            )}
          </div>
          <p className="text-sm font-medium text-foreground line-clamp-1 mt-0.5">{entry.title}</p>
          <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{entry.description}</p>
          <p className="text-[10px] text-muted-foreground mt-1">
            {formatRelativeTime(entry.published_at)}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        "relative bg-card rounded-xl border border-border shadow-sm",
        "hover:shadow-md transition-all duration-200",
        "border-l-4",
        typeConfig.borderColor,
        entry.priority === "critical" && "ring-1 ring-destructive/20",
        entry.priority === "high" && "ring-1 ring-amber-500/20"
      )}
    >
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start gap-3">
          <ChangelogTypeIcon type={entry.type} size="lg" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={cn("text-xs font-semibold uppercase tracking-wider", typeConfig.color)}>
                {typeConfig.label}
              </span>
              {entry.version && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                  {entry.version}
                </Badge>
              )}
              {(entry.priority === "high" || entry.priority === "critical") && (
                <Badge variant={entry.priority === "critical" ? "danger" : "warning"} className="text-[10px] px-1.5 py-0">
                  {entry.priority}
                </Badge>
              )}
            </div>
            <h3 className="text-base font-semibold text-foreground mt-1">{entry.title}</h3>
          </div>
          <span className="text-xs text-muted-foreground shrink-0 mt-1">
            {formatRelativeTime(entry.published_at)}
          </span>
        </div>

        {/* Description */}
        <div className="mt-3 text-sm text-muted-foreground leading-relaxed prose-sm max-w-none">
          <MarkdownContent content={entry.description} />
        </div>

        {/* Tags */}
        {entry.tags.length > 0 && (
          <div className="flex items-center gap-1.5 mt-4 flex-wrap">
            {entry.tags.map((tag) => (
              <Badge key={tag} variant="outline" className="text-[10px] px-2 py-0 font-normal">
                {tag}
              </Badge>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center gap-3 mt-4 text-[11px] text-muted-foreground">
          <span>by {entry.created_by_name}</span>
          {entry.system_ticket && (
            <>
              <span className="text-border">|</span>
              <span>From ticket</span>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function MarkdownContent({ content }: { content: string }) {
  // Split by double newlines for paragraphs, single newlines for line breaks
  const paragraphs = content.split(/\n\n+/)

  return (
    <div className="space-y-2">
      {paragraphs.map((paragraph, i) => {
        // Handle bullet points
        if (paragraph.trim().startsWith("- ") || paragraph.trim().startsWith("* ")) {
          const items = paragraph.split(/\n/).filter(Boolean)
          return (
            <ul key={i} className="list-disc list-inside space-y-0.5">
              {items.map((item, j) => (
                <li key={j}>{item.replace(/^[-*]\s+/, "")}</li>
              ))}
            </ul>
          )
        }

        // Handle bold text (**text**)
        const parts = paragraph.split(/(\*\*[^*]+\*\*)/g)
        return (
          <p key={i}>
            {parts.map((part, j) => {
              if (part.startsWith("**") && part.endsWith("**")) {
                return <strong key={j}>{part.slice(2, -2)}</strong>
              }
              return <span key={j}>{part}</span>
            })}
          </p>
        )
      })}
    </div>
  )
}
